import React, { useEffect, useState } from 'react';
import { Button, ProgressBar } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { UilRocket } from '@iconscout/react-unicons';
import { useNavigate } from 'react-router-dom';
import Unicon from 'components/base/Unicon';
import { BillingInfo } from 'models/Billing';
import { SetupProgress } from 'models/SetupProgress';
import { isProviderBilling, isTrialOrActive } from 'utils/billing';
import { isSetupPopupCompleted, markSetupPopupCompleted } from 'utils/setupPopup';
import { useAuth } from 'hooks/useAuth';
import { useEntities } from 'hooks/useEntities';
import { getSetupProgress } from 'services/setupService';

const TOTAL_STEPS = 3;

const STEP_CONTENT: Record<
  number,
  { title: string; description: string }
> = {
  1: {
    title: 'Account created',
    description: 'Your Shiplab account is ready to go.'
  },
  2: {
    title: 'Add your first connector',
    description: 'Connect a carrier to start automatic invoice collection.'
  },
  3: {
    title: 'Activate your Shiplab trial',
    description:
      'Choose a plan to start your 30-day trial. No charge today; card required.'
  }
};

type SetupStep = {
  number: number;
  title: string;
  description: string;
  complete: boolean;
};

type StepProgressProps = {
  billing?: BillingInfo | null;
  billingLoaded?: boolean;
};

const StepRow = ({ step, isLast }: { step: SetupStep; isLast: boolean }) => (
  <div
    className={`d-flex gap-3 position-relative z-1 ${isLast ? '' : 'mb-4'}`}
  >
    <div
      className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
        step.complete
          ? 'bg-primary'
          : 'border border-primary bg-white text-primary fw-bold fs-10'
      }`}
      style={{ width: '28px', height: '28px' }}
    >
      {step.complete ? (
        <FeatherIcon icon="check" size={16} className="text-white" />
      ) : (
        step.number
      )}
    </div>
    <div className="d-flex flex-grow-1 align-items-start gap-2 min-w-0">
      <div>
        <h6 className="mb-0 fw-bold fs-9 text-dark">{step.title}</h6>
        <p className="mb-0 fs-10 text-secondary">{step.description}</p>
      </div>
    </div>
  </div>
);

const StepProgress = ({
  billing = null,
  billingLoaded = true
}: StepProgressProps) => {
  const { accessToken, username } = useAuth();
  const { connectorsById } = useEntities();
  const [dismissed, setDismissed] = useState(false);
  const [setup, setSetup] = useState<SetupProgress | null>(null);
  const [setupLoaded, setSetupLoaded] = useState(false);
  const navigate = useNavigate();
  const connectorCount = Object.keys(connectorsById).length;
  const popupAlreadyCompleted = isSetupPopupCompleted(username);

  useEffect(() => {
    if (!accessToken) {
      setSetup(null);
      setSetupLoaded(false);
      return;
    }

    let cancelled = false;
    void getSetupProgress(accessToken)
      .then(data => {
        if (!cancelled) {
          setSetup(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSetup(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSetupLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, connectorCount]);

  const firstConnectorAdded = Boolean(setup?.first_connector_added);
  const trialActivated = billing ? isTrialOrActive(billing) : false;
  const providerBilling = billing ? isProviderBilling(billing) : false;
  const setupComplete = trialActivated || providerBilling;

  useEffect(() => {
    if (setupComplete) {
      markSetupPopupCompleted(username);
    }
  }, [setupComplete, username]);

  if (popupAlreadyCompleted || dismissed) {
    return null;
  }

  if (!setupLoaded || !billingLoaded) {
    return null;
  }

  if (setupComplete) {
    return null;
  }

  const steps: SetupStep[] = [
    {
      number: 1,
      ...STEP_CONTENT[1],
      complete: true
    },
    {
      number: 2,
      ...STEP_CONTENT[2],
      complete: firstConnectorAdded
    },
    {
      number: 3,
      ...STEP_CONTENT[3],
      complete: trialActivated
    }
  ];

  const completedSteps = steps.filter(step => step.complete).length;
  const progressPercent = (completedSteps / TOTAL_STEPS) * 100;

  return (
    <div
      className="bg-white rounded-4 shadow-lg p-4 d-flex flex-column"
      style={{
        position: 'fixed',
        left: '1.5rem',
        bottom: 'calc(var(--phoenix-footer-height) + 0.75rem)',
        zIndex: 1045,
        width: '400px',
        border: '1px solid #e3e6ed'
      }}
    >
      <div className="position-absolute" style={{ top: '1.5rem', right: '1.5rem' }}>
        <button
          className="btn btn-link p-0 text-secondary"
          onClick={() => setDismissed(true)}
        >
          <FeatherIcon icon="x" size={20} />
        </button>
      </div>

      <div className="d-flex align-items-start gap-3 mb-3">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
          style={{ width: '48px', height: '48px', backgroundColor: '#fff5ec' }}
        >
          <Unicon icon={UilRocket} size={24} style={{ color: '#fd7e14' }} />
        </div>
        <div className="pe-4">
          <h5 className="mb-1 fw-bold text-dark">Complete your setup</h5>
          <p className="mb-0 fs-10 text-secondary" style={{ lineHeight: '1.4' }}>
            Finish these steps to start automatically collecting carrier invoice
            data.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2 fs-10 fw-bold">
          <span className="text-dark">
            {completedSteps} of {TOTAL_STEPS} steps complete
          </span>
          <span className="text-primary">{Math.round(progressPercent)}%</span>
        </div>
        <ProgressBar
          now={progressPercent}
          style={{ height: '6px', transition: 'width 0.6s ease' }}
        />
      </div>

      <div className="position-relative mb-4">
        <div
          className="position-absolute"
          style={{
            left: '13px',
            top: '28px',
            bottom: '20px',
            width: '2px',
            borderLeft: '2px dashed #e3e6ed',
            zIndex: 0
          }}
        />

        {steps.map((step, index) => (
          <StepRow
            key={step.number}
            step={step}
            isLast={index === steps.length - 1}
          />
        ))}
      </div>

      <div className="d-grid gap-2 mt-2">
        <Button
          variant="primary"
          className="py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
          onClick={() =>
            navigate(
              firstConnectorAdded
                ? '/pages/plans-billing'
                : '/connector-management'
            )
          }
        >
          {!firstConnectorAdded && <FeatherIcon icon="plus" size={18} />}
          {firstConnectorAdded ? 'View plans' : 'Add First Connector'}
        </Button>

        <button
          className="btn btn-link btn-sm text-primary text-decoration-none fw-bold"
          onClick={() => setDismissed(true)}
        >
          Remind me later
        </button>
      </div>
    </div>
  );
};

export default StepProgress;
